"use client";

import { Quote } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useState } from "react";
const stages = [{
  number: "01",
  cefr: "INIT",
  name: "System Boot",
  description: "You begin by initializing your environment — setting up your terminal, learning basic syntax, and writing your first lines of functional code.",
  duration: "~40 hours",
  timeframe: "~2 months",
  quote: "The first time I saw my code actually run, I knew I was hooked for life.",
  quoteAuthor: "Alex Chen, Lead Developer",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/terminal_setup.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/terminal_setup.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/terminal_setup.webp-wvc-sizes",
    alt: "Terminal setup on a dark screen"
  }
}, {
  number: "02",
  cefr: "CORE",
  name: "Logic Flow",
  description: "Variables, loops, and functions become second nature. You start building small scripts that solve real-world problems and automate tasks.",
  duration: "~80 hours",
  timeframe: "~4 months",
  quote: "This is where the logic clicks — you stop memorizing and start thinking like a machine.",
  quoteAuthor: "Sarah Jenkins, Systems Architect",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/logic_flow_diagram.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/logic_flow_diagram.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/logic_flow_diagram.webp-wvc-sizes",
    alt: "Logic flow diagram on a whiteboard"
  }
}, {
  number: "03",
  cefr: "DEV",
  name: "Project Build",
  description: "You move from scripts to full applications. You learn version control, API integration, and how to structure complex codebases.",
  duration: "~120 hours",
  timeframe: "~6 months",
  quote: "Building my first full-stack app was the moment I felt like a real engineer.",
  quoteAuthor: "Marcus Thorne, Senior Dev",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/code_editor_workspace.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/code_editor_workspace.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/code_editor_workspace.png-wvc-sizes",
    alt: "Code editor workspace with multiple files"
  }
}, {
  number: "04",
  cefr: "ARCH",
  name: "Architecture",
  description: "You discover design patterns, clean code principles, and scalability. Your code stops being just functional and starts being professional.",
  duration: "~160 hours",
  timeframe: "~8 months",
  quote: "When you start writing code that others can easily read, you've reached a new level.",
  quoteAuthor: "Elena Rodriguez, Tech Lead",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/system_architecture.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/system_architecture.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/system_architecture.webp-wvc-sizes",
    alt: "System architecture diagram on a screen"
  }
}, {
  number: "05",
  cefr: "PRO",
  name: "Deployment",
  description: "Complex systems, cloud infrastructure, and security. You deploy your projects to the world and manage real-time user traffic.",
  duration: "~200 hours",
  timeframe: "~10 months",
  quote: "Deploying to production is the ultimate test of your skills and your nerves.",
  quoteAuthor: "Alex Chen, Lead Developer",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/server_deployment.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/server_deployment.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/server_deployment.webp-wvc-sizes",
    alt: "Server deployment dashboard"
  }
}, {
  number: "06",
  cefr: "MAST",
  name: "Innovation",
  description: "The technology is yours. You contribute to open source, mentor others, and architect solutions that push the boundaries of what's possible.",
  duration: "~250 hours",
  timeframe: "~12 months",
  quote: "Mastery isn't an endpoint — it's where the real innovation begins.",
  quoteAuthor: "Sarah Jenkins, Systems Architect",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/innovation_lab.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/innovation_lab.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/innovation_lab.png-wvc-sizes",
    alt: "Innovation lab with hardware setups"
  }
}];
function StageRow({
  stage,
  index
}: {
  stage: (typeof stages)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -80px 0px"
  });
  return <div data-index={index} ref={ref} className={`relative flex flex-col items-center gap-8 md:gap-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="flex items-center gap-4 w-full max-w-2xl">
        <div className="font-mono text-4xl md:text-5xl text-primary/20 font-bold tracking-tighter" aria-hidden="true">
          {stage.number}
        </div>
        <div className="h-px flex-1 bg-primary/20" />
      </div>

      <Card className="w-full max-w-2xl overflow-hidden border-border/40 bg-card/50 backdrop-blur-sm shadow-lg">
        <CardContent className="p-0">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-6 md:p-8 order-2 md:order-1">
              <StageCard stage={stage} />
            </div>
            <div className="order-1 md:order-2">
              <StageImage stage={stage} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}
function StageCard({
  stage
}: {
  stage: (typeof stages)[number];
}) {
  return <div className="flex flex-col gap-4 items-start">
      <Badge variant="outline" className="font-mono text-xs tracking-widest uppercase border-primary/40 text-primary px-2.5 py-1 rounded-md">
        LEVEL {stage.cefr}
      </Badge>
      <h3 className="font-default text-2xl md:text-3xl font-medium text-foreground leading-tight">
        {stage.name}
      </h3>
      <p className="font-default text-foreground/80 leading-relaxed" style={{
      fontSize: "16px"
    }}>
        {stage.description}
      </p>
      <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-wider text-muted-foreground pt-1">
        <span>{stage.duration}</span>
        <span className="text-primary/60">·</span>
        <span>{stage.timeframe}</span>
      </div>
      <div className="flex gap-3 pt-4 border-t border-border/60 mt-2 w-full">
        <Quote className="size-5 text-primary/70 shrink-0 mt-1" strokeWidth={1.5} />
        <div className="flex flex-col gap-1">
          <p className="font-default italic text-sm text-foreground/85 leading-snug">
            &ldquo;{stage.quote}&rdquo;
          </p>
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            — {stage.quoteAuthor}
          </span>
        </div>
      </div>
    </div>;
}
function StageImage({
  stage
}: {
  stage: (typeof stages)[number];
}) {
  return <div className="h-full w-full">
      <AspectRatio ratio={1} className="overflow-hidden bg-muted">
        <img src={stage.image.src} data-wvc-srcset={stage.image.srcset} data-wvc-sizes={stage.image.sizes} alt={stage.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
      </AspectRatio>
    </div>;
}
export default function HubEvolution() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById("hub-evolution");
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionHeight = rect.height;
      const scrolled = Math.min(Math.max((windowHeight - rect.top) / (sectionHeight + windowHeight), 0), 1);
      setScrollProgress(scrolled * 100);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="656" id="hub-evolution" className="relative bg-background text-foreground overflow-hidden py-24 md:py-40">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] select-none" aria-hidden="true">
        <span className="absolute top-[8%] left-[6%] font-default text-[140px] text-foreground">
          &lt;/&gt;
        </span>
        <span className="absolute top-[28%] right-[8%] font-default italic text-[180px] text-foreground">
          {`{ }`}
        </span>
        <span className="absolute top-[52%] left-[12%] font-default text-[160px] text-foreground">
          #
        </span>
        <span className="absolute top-[72%] right-[10%] font-default italic text-[200px] text-foreground">
          &gt;_
        </span>
        <span className="absolute bottom-[6%] left-[8%] font-default text-[150px] text-foreground">
          $
        </span>
      </div>

      {}
      <div className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-20 flex-col items-center gap-3 pointer-events-none">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground writing-mode-vertical">
          Progress
        </span>
        <div className="relative h-48 w-px bg-border">
          <div className="absolute top-0 left-0 w-full bg-primary transition-all duration-150 ease-out" style={{
          height: `${scrollProgress}%`
        }} />
          <div className="absolute left-1/2 -translate-x-1/2 size-2 rounded-lg bg-primary transition-all duration-150 ease-out" style={{
          top: `calc(${scrollProgress}% - 4px)`
        }} />
        </div>
        <span className="font-mono text-[10px] tabular-nums text-primary">
          {Math.round(scrollProgress)}%
        </span>
      </div>

      <div className="container mx-auto px-6 md:px-10 max-w-6xl relative">
        {}
        <div ref={headerReveal.ref} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-20 md:mb-28 transition-all duration-700 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-primary mb-6">
            The Path to Mastery
          </span>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-medium text-foreground leading-[1.1] tracking-tight mb-6">
            From{" "}
            <span className="italic">first line</span> to{" "}
            <span className="italic text-primary">full-stack mastery.</span>
          </h2>
          <p className="font-default text-foreground/75 leading-relaxed max-w-2xl" style={{
          fontSize: "18px"
        }}>
            A six-stage roadmap designed for Code Hub members — track your growth, 
            master the stack, and build the future.
          </p>
        </div>

        {}
        <div className="relative max-w-[800px] mx-auto">
          <div className="flex flex-col gap-16 md:gap-24 relative">
            {stages.map((stage, i) => <StageRow stage={stage} index={i} key={i} />)}
          </div>
        </div>
      </div>
    </section>;
}